<template>
	<div id="acquisit-bankid" :tabindex="0" :aria-label="language.parse(ariaLabel)">
		<div :class="['auth-wrapper', { ['status-' + status]: status }]" tabindex="-1" aria-hidden="true">
			<div class="auth" :style="{ color: authColor?.cssHSLA }">
				<v-svg v-if="status == BANKID_STATUS_SUCCESS" class="icon" file="acq/authorized" multi-color />
				<v-svg v-if="status == BANKID_STATUS_ERROR" class="icon" file="acq/unauthorized" multi-color />

				<template v-if="status == BANKID_STATUS_ERROR">
					<div class="message error">
						<acquisit-string class="text" :source="authMessage" />
					</div>
				</template>

				<template v-else-if="status == BANKID_STATUS_SUCCESS">
					<div class="message success">
						<acquisit-string class="text" :source="authMessage" />
						<span class="meta">
							<span class="item" v-if="authName">
								<v-svg class="icon" file="acq/person" />
								{{ authName }}
							</span>

							<span class="item" v-if="authDateFormatted">
								<v-svg class="icon" file="acq/clock-check-outline" />
								{{ authDateFormatted }}
							</span>
						</span>
					</div>
				</template>
				
				<template v-else-if="status == BANKID_STATUS_WAITING_FOR_INPUT">
					<div class="message inputs" role="form">
						<div class="logo" role="presentation">
							signerer med
							<img src="/images/bankid.svg" alt="BankID" />
						</div>
						
						<div class="person-number-input" role="presentation">
							<acquisit-input v-model="personNumber"
							                format="DDDDDD-DDDDD"
							                :has-current-errors="inputsSubmitStatus == 'error'"
							                :errors="inputsErrors"
							                @keyup.enter="onClickOpenBankID"
							                :disabled="inputsSubmitted"
							                :no-interact="inputsSubmitted"
							                label="Fødselsnummer" />
							
							<ProgressBorder class="submit-button-progress-border"
							                :progress="submitButtonProgress"
							                :color="submitButtonConfig.color">
								<acquisit-pill-button @click="onClickOpenBankID"
								                      :color="submitButtonConfig.color"
								                      class="submit-button"
								                      label="Gå videre"
								                      :loading="inputsSubmitted"
								                      :disabled="inputsSubmitted"
								                      :no-interact="inputsSubmitted"
								                      :icon="submitButtonConfig.icon"
								                      :side="submitButtonConfig.side" />
							</ProgressBorder>
						</div>
						
						<div class="actions" role="presentation">
							<acquisit-button type="text"
							                 icon="arrows/left"
							                 label="Tilbake"
							                 @click="onClickReset" />
						</div>
					</div>
				</template>

				<template v-else>
					<div class="message waiting">
						<span class="text">Venter…</span>
					</div>
				</template>
				
				<div class="focus-ring"></div>
			</div>
		</div>
		
		<div class="signature-actions" v-if="status !== BANKID_STATUS_WAITING_FOR_INPUT">
			<acquisit-button type="flat" bordered
			                 color="red"
			                 icon="acq/reset"
			                 :label="resetLabel"
			                 @click="onClickReset" />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { type PropType } from 'vue'
	import { useBackend } from '@/lib/backend-cors'
	import { useAPIStore } from '@/stores/api'
	import { type Person, type UILabelOptional, type UILabel, useRaygun, ValidationError } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
	import { useComponentLogger } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
	import { useBaseComponentProps, useBaseComponentEmits, useBaseComponent } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/helpers'
	import { timestampToHumanString } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
	import { computed, onMounted, ref, watch } from 'vue'
	import { parseSignature, usePersonsStore } from '@/stores/persons'
	import type { ParsedBankIDSignature } from '@/lib/types'
	import { encryptAES_GCM, isCryptoAvailable } from '@/lib/functions/crypto'
	import { errorMessage } from '@/lib/ui'
	import { useUIStore } from '@/stores/ui'
	import { useRoute, useRouter } from 'vue-router'
	import { useProtocolStore } from '@/stores/protocol'
	import { useGenericStore } from '@/stores/generic'
	import { usePendingTasksStore } from '@/stores/pending-tasks.ts'
	import ProgressBorder from '@ui/ProgressBorder.vue'
	
	const BANKID_STATUS_SUCCESS = 'success'
	const BANKID_STATUS_ERROR = 'error'
	const BANKID_STATUS_WAITING_FOR_INPUT = 'waiting-for-input'
	
	const props = defineProps({
		...useBaseComponentProps(),
		
		person: {
			type: Object as PropType<Person>,
			required: true
		},
		
		mode: {
			type: String,
			required: false,
			default: 'init',
			validator: (val: any) => ['init', 'verify'].includes(val)
		},
		
		resetLabel: {
			type: [String, Object] as PropType<UILabel>,
			default: null
		},
		
		successLabel: {
			type: [String, Object] as PropType<UILabel>,
			default: null
		},
		
		errorLabel: {
			type: [String, Object] as PropType<UILabel>,
			default: null
		},
		
		modelValue: {
			type: String,
			required: false,
			default: undefined
		}
	})
	
	const emit = defineEmits([
		...useBaseComponentEmits(),
		'update:modelValue',
		'reset'
	])
	
	const base = useBaseComponent(props, emit)
	const { color, language } = base
	
	const log = useComponentLogger('components/ui/extended/BankId', props)
	
	const apiStore = useAPIStore()
	const protocolStore = useProtocolStore()
	const pendingTasksStore = usePendingTasksStore()
	
	const $router = useRouter(),
		$route = useRoute()
	
	const personNumber = ref<string>('')
	const authMessage = ref<UILabelOptional>()
	const authName = ref<string|null>()
	const authDate = ref<Date|null>()
	const status = ref<string|null>()
	
	const inputsSubmitStatus = ref<'ready'|'error'>('ready')
	const inputsErrors = ref<ValidationError[]>([])
	const inputsSubmitted = ref(false)
	
	const method = computed(() => {
		if (props.person.signature_type?.type == 'bankid_mobile') {
			return 'no-bid-m'
		}
		
		return 'no-bid'
	})
	
	const authDateFormatted = computed(() => {
		if (!(authDate.value instanceof Date)) {
			return null
		}
		
		return timestampToHumanString(authDate.value)
	})
	
	const authColor = computed(() => {
		if (authMessage.value == props.successLabel) {
			return color.parse('#222222')
		} else if (authMessage.value == props.errors) {
			return color.parse('#d32e2e')
		}
		
		return undefined
	})
	
	const ariaLabel = computed(() => {
		switch (status.value) {
			case BANKID_STATUS_SUCCESS:
				return authName.value ?
				       authMessage.value + ' som ' + authName.value :
				       authMessage.value
			
			case BANKID_STATUS_ERROR:
				return authMessage.value
			
			default:
				return 'Vennligst venter…'
		}
	})
	
	const submitButtonConfig = computed(() => {
		if (inputsSubmitStatus.value == 'error') {
			return {
				color: 'red',
				side: 'left',
				icon: 'acq/cross'
			}
		}
		
		return {
			color: color.baseContextSafe.value,
			side: 'right',
			icon: 'arrows/right'
		}
	})
	
	const submitButtonProgress = computed(() => {
		if (!inputsSubmitted.value || pendingTasksStore.pendingCount === 0) {
			return null
		}
		
		return pendingTasksStore.aggregateProgress
	})
	
	const updateAuthData = () => {
		if (props.person.signature) {
			const attributes = parseSignature(props.person.signature) as ParsedBankIDSignature|null
			
			if (attributes) {
				authName.value = attributes.name || null
				authDate.value = attributes.timestamp ? new Date(attributes.timestamp * 1000) : null
			}
		}
	}
	
	const createReturnURL = async () => {
		// Get URL to redirect to, append information to restore when the user comes back
		const currentURL = new URL(window.location.href)
		currentURL.searchParams.delete('bid')
		currentURL.searchParams.delete('sessionId')
		currentURL.searchParams.delete('status')
		
		// Gather selected persons and their info
		const queryInfo: any[] = []
		let selected = usePersonsStore().selected
		
		for (let person of selected) {
			queryInfo.push({
				id: person.id,
				on_behalf: person.on_behalf
			})
		}
		
		const queryJSON = JSON.stringify(queryInfo)
		
		if (isCryptoAvailable()) {
			const password = (useAPIStore().token ?? 'default').substring(0, 7)
			const queryCrypted = await encryptAES_GCM(queryJSON, password)
			currentURL.searchParams.set('bid', encodeURIComponent(queryCrypted))
		} else {
			// No crypto available, probably because of local. Warn about that.
			console.warn('crypto is not available')
			
			const queryEncoded = window.btoa(JSON.stringify(queryInfo))
			currentURL.searchParams.set('bid', encodeURIComponent(queryEncoded))
		}
		
		return currentURL
	}
	
	const onClickOpenBankID = async () => {
		if (!personNumber.value || personNumber.value.length < 12) {
			inputsSubmitStatus.value = 'error'
			inputsErrors.value = [
				new ValidationError('Person number missing', null)
			]
			
			setTimeout(() => {
				inputsSubmitStatus.value = 'ready'
			}, 800)
			
			return
		}
		
		await openBankID()
	}
	
	const openBankID = async () => {
		if (useAPIStore().isPreview) {
			alert('BankID signature is unavailable in previews. Simulation will assume a successful signature.')
			emit('update:modelValue', createDummySignature())
			
			authMessage.value = props.successLabel
			status.value = BANKID_STATUS_SUCCESS
			
			updateAuthData()
			return
		}
		
		if ((import.meta.env.DEV || import.meta.env.ACQUISIT_MODE == 'staging') && !protocolStore.backed_up) {
			await errorMessage(
				'BankID signature requires that the protocol has been backed up at least once.\n\nPlease save a page before continuing.',
				'Cannot sign with BankID',
			)
			
			setError()
			
			useGenericStore().backup_enabled = true
			return
		}
		
		const currentURL = new URL(self.location.href)
		
		if (currentURL.protocol === 'http:' && import.meta.env.DEV) {
			currentURL.protocol = 'https:'
			await errorMessage('BankID requires HTTPS. <a href="' + currentURL.href + '">Switch to HTTPS</a>')
			return
		}
		
		if (inputsSubmitted.value) {
			return
		}
		
		inputsSubmitted.value = true
		
		const returnURL = await createReturnURL()
		const pn = personNumber.value.replace(/-/g, '')
		const bankIDURL = useBackend().getBankIDURL(apiStore.token!, props.person.id, pn, returnURL.href)
		
		log.debugFrom('openBankID', bankIDURL)
		log.debugFrom('openBankID', 'Return to', returnURL.href)
		
		if (pendingTasksStore.pendingCount > 0) {
			log.debugFrom('openBankID', 'Waiting for pending tasks to finish…')
			
			await pendingTasksStore.whenIdle()
		}
		
		window.location.href = bankIDURL
	}
	
	/**
	 * Verify the current session if there is one and consume it, replacing with a cleaner URL after
	 * (determined by query params "status" and "sessionId")
	 */
	const verifyCurrentSession = async () => {
		log.debugFrom('verifyCurrentSession', 'Fetching data')
		
		const currentURL = new URL(window.location.href)
		const returnStatus = currentURL.searchParams.has('status') ? currentURL.searchParams.get('status') : 'abort'
		const sessionID = currentURL.searchParams.has('sessionId') ? currentURL.searchParams.get('sessionId') : null
		
		if (returnStatus === 'success' && sessionID) {
			useUIStore().setLoader(true)
			
			try {
				const data = await useBackend().getBankIDData(apiStore.token!, props.person.id, sessionID)
				
				const commonName = data.common_name,
					personNumber = data.person_number,
					timestamp = data.timestamp
				
				const signature = `bankid:${commonName};${personNumber};${timestamp}`
				
				status.value = BANKID_STATUS_SUCCESS
				authMessage.value = props.successLabel
				authName.value = commonName
				authDate.value = new Date(timestamp * 1000)
				
				emit('update:modelValue', signature)
				log.infoFrom('verifyCurrentSession', 'Verified')
				
				// Remove consumed query params
				await removeSessionQueryParams()
			} catch (e: any) {
				log.always.errorFrom('verifyCurrentSession', 'Could not verify session', e)
				setError()
				
				emit('update:modelValue', null)
				useRaygun().send(e, ['bankID'])
			} finally {
				useUIStore().setLoader(false)
			}
		} else {
			// error or abort
			setError()
			emit('update:modelValue', null)
		}
	}
	
	const createDummySignature = () => {
		const pn = personNumber.value?.length ? personNumber.value : '010101-12345'
		return `bankid:${props.person.firstname} ${props.person.name};${pn};${Math.round(+new Date() / 1000)}`
	}
	
	const removeSessionQueryParams = async () => {
		await $router.replace({
			...$route,
			query: {
				...$route.query,
				bid: undefined,
				sessionId: undefined,
				status: undefined
			}
		})
	}
	
	const retryAuth = () => {
		reset()
	}
	
	const onClickReset = () => {
		emit('reset')
	}
	
	const setError = () => {
		authMessage.value = props.errorLabel
		status.value = BANKID_STATUS_ERROR
		authDate.value = null
		authName.value = null
	}
	
	const reset = () => {
		authMessage.value = undefined
		status.value = null
		authDate.value = null
	}
	
	onMounted(() => {
		if (props.mode == 'verify') {
			verifyCurrentSession()
		} else if (props.person.signature) {
			// Person is signed already, display success message
			authMessage.value = props.successLabel
			status.value = BANKID_STATUS_SUCCESS
		} else {
			// Set status to waiting for input so the user can enter required information
			status.value = BANKID_STATUS_WAITING_FOR_INPUT
		}
	})
	
	watch(() => props.person, () => {
		updateAuthData()
	}, {
		immediate: true
	})
	
	watch(personNumber, () => {
		inputsErrors.value = []
	})
	
	defineExpose({
		...base.expose,
		retryAuth,
		openBankID,
	})
</script>

<style lang="scss" scoped>
	@use '@/assets/mixins.scss' as m;

	#acquisit-bankid {
		outline: 0;
		
		iframe {
			position: fixed;
			top: 0;
			left: 0;
			background: white;
			width: 100%;
			height: 100%;
			z-index: 9999;
		}

		.auth-wrapper {
			height: 150px;
			position: relative;
			background: m.color("light-background");
			transition: background 0.2s;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 20px;
			container: wrapper / inline-size;

			@include m.media(mobile) {
				margin: {
					left: -24px;
					right: -24px;
				}
			}

			.auth {
				display: flex;
				align-items: center;
				margin-left: -20px;

				> .icon {
					margin: 10px;
					width: 42px;
					height: 42px;

					:deep(svg) {
						width: inherit;
						height: inherit;
					}
				}

				.message {
					display: block;
					font-size: 18px;
					font-weight: 400;

					.text,
					.meta {
						display: block;
					}

					.text + .meta {
						margin-top: 12px;
					}

					.meta {
						font-size: 0.9rem;
						color: m.color("light-grey-blue");

						.icon {
							width: 16px;
							height: 16px;
							display: inline-flex;
							vertical-align: middle;
							margin-top: -3px;
						}

						.item {
							display: block;

							.icon {
								margin-right: 4px;
							}

							& + .item {
								margin-top: 8px;
							}
						}
					}
				}
			}
			
			&.status-error {
				background: rgba(m.color("red"), 0.067);
			}

			&.status-success {
				background: m.color("light-background");
				
				.auth {
					align-items: flex-start;
					padding: {
						top: 4px;
					}
					
					.icon {
						margin-top: -21px + 6px;
					}
				}
			}
			
			&.status-waiting-for-input {
				display: block;
				
				height: auto;
				background: transparent;
				
				padding: {
					left: 12px;
					right: 12px;
				}
				
				margin-bottom: 36px;
				
				@include m.media(mobile) {
					margin-left: 0;
					margin-right: 0;
				}
				
				.auth {
					margin: 0;
				}
				
				.acquisit-component {
					width: 100%;
				}
				
				.inputs {
					width: 100%;
					
					.logo {
						display: flex;
						align-items: center;
						gap: 8px;
						
						color: v-bind("color.textDark.value.cssHSLA");
						
						margin: {
							top: -24px;
							bottom: 24px;
							left: 24px + 20px + 1px;
						}
						
						img {
							height: 16px;
						}
					}
					
					.person-number-input {
						display: flex;
						align-items: center;
						gap: 16px;
						
						.acquisit-input {
							flex: 1;
						}
					}
					
					.actions {
						margin-top: 16px;
					}
					
					.submit-button {
						position: relative;
						
						:deep(.wrapper) {
							padding-left: 8px;
							padding-right: 8px;
						}
					}
				}
				
				@container wrapper (max-width: 350px) {
					
					.inputs {
						
						.person-number-input {
							flex-direction: column;
							justify-content: stretch;
							margin-bottom: 32px;
						}
						
						.acquisit-input {
							width: 100%;
						}
						
						.submit-button {
							width: 100%;
						}
					}
				}
			}
		}
		
		.signature-actions {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			gap: 16px;
		}
		
		.focus-ring {
			@include m.focus-ring;
			
			top: 8px;
			bottom: 8px;
			left: 8px;
			right: 8px;
		}
		
		&:focus .focus-ring {
			@include m.focus-ring-visible;
		}
	}

	@keyframes error {
		10%, 90% {
			transform: translate3d(-1px, 0, 0);
		}

		20%, 80% {
			transform: translate3d(2px, 0, 0);
		}

		30%, 50%, 70% {
			transform: translate3d(-4px, 0, 0);
		}

		40%, 60% {
			transform: translate3d(4px, 0, 0);
		}
	}
</style>
